/**
 * funnelBands.ts
 * 4-band discrete color scale and leak formatting helpers for the Activation Funnel.
 *
 * Band logic (server retention_ratio), aligned to the page's own caption
 * "darker = healthier, warmer = where you're bleeding" (Decision D0.3 —
 * the previous palette coloured HIGH retention red, contradicting the caption
 * every operator reads):
 *   ≥ 0.75 → deep teal   — high retention (healthy)
 *   ≥ 0.50 → teal
 *   ≥ 0.25 → orange      — warming: attention
 *   < 0.25 → red         — bleeding
 */

import { brandColors } from '../../theme/brandColors';

// Band colours, cool→warm as retention falls. Named locally rather than
// re-purposing brandColors.bandRetained (which is red) — the theme file is a
// shared surface this page must not repaint.
export const BAND_HEALTHY  = '#134B60'; // deep teal  — 75–100% retained
export const BAND_GOOD     = '#1A5F7A'; // teal       — 50–75%
export const BAND_WARN     = '#E07A1F'; // orange     — 25–50%
export const BAND_BLEED    = '#B0202F'; // red        — 0–25% (leaking)

export const BAND_LEGEND = [
  { label: '75–100% retained', color: BAND_HEALTHY, textColor: '#fff' },
  { label: '50–75% retained',  color: BAND_GOOD,    textColor: '#fff' },
  { label: '25–50% retained',  color: BAND_WARN,    textColor: '#fff' },
  { label: '0–25% (leaking)',  color: BAND_BLEED,   textColor: '#fff' },
];

/**
 * Step has telemetry when the API says so. The server distinguishes a true
 * in-range zero (has_event:true, value:0 — renders 0) from never-seen
 * (has_event:false — renders "Awaiting telemetry"); the old client-side
 * `|| value > 0` override made the server's signal non-authoritative and the
 * two cases indistinguishable.
 */
export function stepHasData(step: { has_event: boolean; value: number | null }): boolean {
  return step.has_event;
}

/**
 * Retention for band coloring — the server's ratio ONLY. The old three-tier
 * fallback silently switched denominators (step-over-step, then share of
 * installs) under one "retained" legend, so two identically-coloured tiles
 * could be measuring different things. No ratio → awaiting-telemetry gray.
 */
export function resolveRetentionRatio(
  step: { retention_ratio: number | null },
): number | null {
  return step.retention_ratio;
}

// Data-check amber: the server ships retention UNCLAMPED (design D3.2), so a
// ratio above 1 is a data-quality fact this scale must show, never repaint as
// "healthy". Matches the staircase's data-check chip.
export const BAND_CHECK = '#B45309';

export function getBandColor(retentionRatio: number | null): string {
  if (retentionRatio == null) return '#E5E7EB'; // gray awaiting-telemetry
  if (retentionRatio > 1.0001) return BAND_CHECK;
  if (retentionRatio >= 0.75) return BAND_HEALTHY;
  if (retentionRatio >= 0.50) return BAND_GOOD;
  if (retentionRatio >= 0.25) return BAND_WARN;
  return BAND_BLEED;
}

export function getBandTextColor(retentionRatio: number | null): string {
  // #4B5563, not #9CA3AF: a live tile can carry a null ratio (first step of a
  // sequence on an empty cohort) and the lighter gray was near-invisible on
  // the gray tile background.
  if (retentionRatio == null) return '#4B5563';
  return '#FFFFFF'; // every live band is dark enough for white text
}

export function fmtLeak(
  lost: number | null,
  lostStepPct: number | null,
): string {
  if (lost == null) return '—';
  // Negative lost = this step counted MORE members than its predecessor —
  // unclamped by design; say what it is instead of printing "0 lost".
  if (lost < 0) return `⚠ ${Math.abs(lost).toLocaleString()} above prev step — data check`;
  if (lost === 0) return '↓ 0 lost';
  const parts = [`↓ ${lost.toLocaleString()} lost`];
  if (lostStepPct !== null) parts.push(`${lostStepPct.toFixed(1)}% of step`);
  // lost_total_pct is still in the response but no longer rendered here —
  // each tile carries exactly ONE "of total" figure (fmtPctOfTotal), where two
  // differently-derived ones used to sit on adjacent lines.
  return parts.join(' · ');
}

export function fmtDwell(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
}

export function fmtPctOfTotal(value: number | null, funnelTop: number): string {
  if (value === null || funnelTop === 0) return '—';
  return `${((value / funnelTop) * 100).toFixed(1)}% of new Members`;
}

// Phase accent colors for Phase Flow chart
export const PHASE_COLORS: Record<number, string> = {
  1: '#1A5F7A', // deepTeal
  2: '#146152', // forestGreen
  3: '#2A7F9A', // deepTealLight
  4: '#7D1935', // richBurgundy
  5: '#D4AC0D', // warmGold
  6: '#5D6D7E', // slateGray
};

// ─── Shared tokens for the enhanced Activation Funnel sections ──────────────────
// Reuse the admin app's already-loaded font families (no new fonts added):
// Playfair Display (titles), Montserrat (UI/CTAs), Source Sans Pro (body),
// Fira Code (mono — in lieu of the mock's IBM Plex Mono).
export const FONT = "'Montserrat', sans-serif";
export const FONT_H = "'Playfair Display', serif";
export const FONT_BODY = "'Source Sans Pro', sans-serif";
export const FONT_MONO = "'Fira Code', monospace";

export const AWAITING_LABEL = 'Awaiting telemetry';

// The date-filter option list that used to live here is gone: the whole admin
// dashboard now renders `components/common/TimeRangeFilter` over DAY_PRESETS.

// Brand surface tokens (mirror the design mock; values from brandColors where shared)
export const SURFACE = {
  cream: '#F9F3E9',
  paper: '#FFFDF8',
  line: '#E7DDCA',
  ink: '#1A1A1A',
  slate: '#5D6D7E',
};

// Sign-up method colors (Section 2). Keyed by the API's STABLE provider key,
// not the display label — renaming a label must not silently drop a brand
// colour. Unknown providers take a neutral slate.
export const METHOD_COLORS: Record<string, string> = {
  google:   '#1A5F7A',
  email:    '#D4AC0D',
  apple:    '#1A1A1A',
  facebook: '#1877F2',
  yahoo:    '#5D6D7E',
};

// Milestone / spark-funnel stage colors (Section 1), descending narrative.
export const MILESTONE_COLORS: Record<string, string> = {
  install: '#1A5F7A',        // deepTeal — acquisition
  account: '#2A7F9A',        // deepTealLight
  first_chapter: '#146152',  // forestGreen — first value
  second_chapter: '#8A9A5B', // olive — habit forming
  subscriber: '#D4AC0D',     // warmGold — subscriber
};

// Status chip palette (staircase) — {bg, fg}.
export const STATUS_CHIP: Record<string, { bg: string; fg: string }> = {
  healthy:     { bg: '#e6f0ec', fg: '#146152' },
  'data check': { bg: '#fdf0e3', fg: '#B45309' },
  'leakiest hop': { bg: '#f3e3e7', fg: '#7D1935' },
  watch:       { bg: '#fbf0db', fg: '#b06a00' },
  retention:   { bg: '#eef1f3', fg: '#5D6D7E' },
};

// Leak-card severity palette — {bg, fg, border}.
export const SEVERITY_CHIP: Record<string, { bg: string; fg: string; border: string }> = {
  Critical: { bg: '#f3e3e7', fg: '#7D1935', border: '#7D1935' },
  High:     { bg: '#fbf0db', fg: '#b06a00', border: '#b06a00' },
  Medium:   { bg: '#fbf7ee', fg: '#a8870a', border: '#D4AC0D' },
  Minor:    { bg: '#eef1f3', fg: '#5D6D7E', border: '#5D6D7E' },
};

// Leak four-count cell colors (Entered / In-flight / Confirmed lost / Moved on).
export const LEAK_COUNT_COLORS = {
  entered: '#1A5F7A',
  flight: '#D4AC0D',
  lost: '#7D1935',
  moved: '#146152',
};

/** Health color for a step-over-step / hop-survival percentage (0–100). */
export function convPctColor(pct: number | null | undefined): string | null {
  // == null also swallows undefined — a version-skewed API response missing
  // the field must degrade to "no chip", not paint red and then crash toFixed.
  if (pct == null) return null;
  return pct >= 60 ? '#146152' : pct >= 30 ? '#b06a00' : '#7D1935';
}

/** One human form for a staleness window, shared by the page header and the
 *  Leak Ledger (the two used to disagree at exactly 24h: "24h" vs "1d"). */
export function fmtWindowHours(hours: number): string {
  if (hours === 24) return '24h';
  if (hours % 24 === 0) return `${hours / 24}d`;
  return `${hours}h`;
}

/**
 * Largest-remainder share strings for a set of counts against one base:
 * the displayed integers sum to exactly 100 when the counts cover the base.
 * Rows under 0.5% (but non-zero) render one decimal instead of a false "0%"
 * — and are EXCLUDED from the integer point distribution, so a decimal row
 * can no longer consume a leftover point and push the visible sum to 99.
 * This is THE percentage formatter for provider shares — both surfaces that
 * show them must use it (two rounding algorithms once printed 43% and 42%
 * for the same provider on one page).
 */
export function formatShares(values: number[], base: number): string[] {
  if (base <= 0) return values.map(() => '—');
  const pcts = values.map(v => (v / base) * 100);
  const isDecimal = pcts.map((p, i) => values[i] > 0 && p < 0.5);
  const floors = pcts.map((p, i) => (isDecimal[i] ? 0 : Math.floor(p)));
  const intExact = pcts.reduce((sum, p, i) => sum + (isDecimal[i] ? 0 : p), 0);
  let leftover = Math.round(intExact) - floors.reduce((a, b) => a + b, 0);
  const order = pcts
    .map((p, i) => ({ i, frac: p - Math.floor(p) }))
    .filter(({ i }) => !isDecimal[i])
    .sort((a, b) => b.frac - a.frac);
  const rounded = [...floors];
  for (const { i } of order) {
    if (leftover <= 0) break;
    rounded[i] += 1;
    leftover -= 1;
  }
  return pcts.map((p, i) => (isDecimal[i] ? `${p.toFixed(1)}%` : `${rounded[i]}%`));
}
